import { Suspense, lazy } from 'react';
import type { ReactNode } from 'react';
import { HomeScreen } from '../ui/home/HomeScreen';
import { resolveGameRoute, resolveHomeNotice, resolvePrivacyPage } from './gameRoute';
import { LazyAppBoundary } from './LazyAppBoundary';
import { loadWarsApp } from './lazyApps';
import { NewVersionPrompt } from './NewVersionPrompt';

/**
 * Survival — the Preserve (ADR-040): its world, shell, HUD and Pixi in a lazily loaded chunk,
 * mounted by its hidden link `?game=preserve` (and the DEV surfaces). The front page's eager
 * bundle never contains it (`tools/checkProdBundle.mjs`).
 */
const PreserveApp = lazy(() => import('./PreserveApp').then((m) => ({ default: m.PreserveApp })));

/**
 * Slime Wars (ADR-037): its own app in a lazily loaded chunk. Neither the front page nor the
 * Preserve ever contains it (`tools/checkProdBundle.mjs`), and its page never mounts the
 * Preserve's world, shell runtime or HUD.
 */
const WarsApp = lazy(() => loadWarsApp().then((m) => ({ default: m.WarsApp })));

/**
 * W059: the privacy page (`/privacy`): plain text, a small lazily loaded chunk of its own — the
 * front page's eager bundle stays as it is.
 */
const PrivacyPage = lazy(() =>
  import('../ui/privacy/PrivacyPage').then((m) => ({ default: m.PrivacyPage })),
);

const PRIVACY = resolvePrivacyPage();
const ROUTE = resolveGameRoute();
const HOME_NOTICE = resolveHomeNotice();

/** A lazily loaded app: a calm loading line while its chunk arrives, a retry if it fails. */
function LazyApp({ name, children }: { readonly name: string; readonly children: ReactNode }) {
  return (
    <LazyAppBoundary name={name}>
      <Suspense
        fallback={
          <p className="app-loading" role="status">
            {`Loading ${name}…`}
          </p>
        }
      >
        {children}
      </Suspense>
    </LazyAppBoundary>
  );
}

/**
 * The page (ADR-040): the front page — a light mode picker that loads neither game — or one of
 * the two games, each its own lazily loaded app. Switching between them is a full navigation.
 * Every page carries the "new version, please reload" prompt (W032, `version.ts`).
 */
export function App() {
  return (
    <>
      <Route />
      <NewVersionPrompt />
    </>
  );
}

function Route() {
  if (PRIVACY) {
    return (
      <LazyApp name="Privacy">
        <PrivacyPage />
      </LazyApp>
    );
  }
  if (ROUTE === 'wars') {
    return (
      <LazyApp name="Slime Wars">
        <WarsApp />
      </LazyApp>
    );
  }
  if (ROUTE === 'preserve') {
    return (
      <LazyApp name="Survival">
        <PreserveApp />
      </LazyApp>
    );
  }
  return <HomeScreen notice={HOME_NOTICE} />;
}
